import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCrm } from '../store';
import { toast } from 'sonner';
import { X, Save, Loader2, Eye, Download } from 'lucide-react';
import type { ContratArtisteRow } from '../pages/ContratsArtistes';
import type { ContratArtisteData } from '../contract/contentArtiste';
import { DEMO_ARTISTE_DATA } from '../contract/contentArtiste';
import { withDemoFlag } from '../demoMode';
import { TvaSelect } from './TvaSelect';
import { normalizeAdresse } from '@/crm/utils/adresse';

interface Props {
  existing: ContratArtisteRow | null;
  prefill?: { prestation_id?: string | null; artiste_id?: string | null } | null;
  onClose: () => void;
  onSaved: () => void;
}

const INPUT_CLS = 'w-full px-3 py-2 bg-slate-900 border border-slate-800 rounded-lg text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-amber-500/40';

function Field({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <label className={`block ${full ? 'sm:col-span-2' : ''}`}>
      <span className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</span>
      {children}
    </label>
  );
}


type ArtisteRow = {
  id: string; nom_scene: string; nom?: string; prenom?: string;
  email?: string; tel?: string; adresse?: string; cp?: string; ville?: string;
  siret?: string; statut_juridique?: string; tva_pct?: number;
  tarif_base?: number | null;
};

function getOptionsRemuneration(calc: any): string {
  if (!calc) return '';
  const fmtEUR = (n: number) => `${(Math.round(n * 100) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  const labels: string[] = [];
  for (let n = 1; n <= 4; n++) {
    if (calc?.[`opt${n}On`]) {
      const rawLabel = String(calc[`opt${n}Lib`] || '').trim() || `Option ${n}`;
      const label = /^option\b/i.test(rawLabel) ? rawLabel : `Option ${rawLabel}`;
      const tarif = Number(calc[`opt${n}Tarif`]) || 0;
      const libre = Number(calc[`opt${n}HT`]) || 0;
      const montant = libre > 0 ? libre : tarif;
      labels.push(montant > 0 ? `Comprenant ${label} (${fmtEUR(montant)} HT)` : `Comprenant ${label}`);
    }
  }
  const frais: [string, string][] = [['fraisDepl','Frais de déplacement'],['vhrDepl','Frais de déplacement (VHR)'],['vhrHotel','Frais d’hébergement'],['vhrProd','Frais de restauration / production']];
  frais.forEach(([k, lib]) => { const m = Number(calc?.[k]) || 0; if (m > 0) labels.push(`${lib} (${fmtEUR(m)} HT)`); });
  return labels.join('\n');
}

/** Frais annexes (déplacement, VHR) saisis dans le calculateur — inclus dans la rémunération artiste. */
function fraisAnnexesHT(calc: any): number {
  if (!calc) return 0;
  return ['fraisDepl', 'vhrDepl', 'vhrHotel', 'vhrProd'].reduce((s, k) => s + (Number(calc[k]) || 0), 0);
}


const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
async function resolvePrestationDbId(pid: string | null | undefined): Promise<string | null> {
  if (!pid) return null;
  if (UUID_RE.test(pid)) return pid;
  const { data } = await supabase.from('prestations').select('id').eq('numero', pid).maybeSingle();
  return (data as any)?.id || null;
}

export default function ContratArtisteForm({ existing, prefill, onClose, onSaved }: Props) {
  const data = useCrm();
  const [saving, setSaving] = useState(false);
  const [artistes, setArtistes] = useState<ArtisteRow[]>([]);
  const [prestArtisteIds, setPrestArtisteIds] = useState<string[]>([]);
  const [prestArtisteCalcMap, setPrestArtisteCalcMap] = useState<Record<string, any>>({});
  const [prestationDbId, setPrestationDbId] = useState<string | null>(null);

  const initial: ContratArtisteData = existing?.contenu || { ...DEMO_ARTISTE_DATA };
  const [form, setForm] = useState<ContratArtisteData & { prestation_id: string | null; artiste_id: string | null; statut: string; notes: string }>({
    ...DEMO_ARTISTE_DATA,
    ...initial,
    prestation_id: existing?.prestation_id || prefill?.prestation_id || null,
    artiste_id: existing?.artiste_id || prefill?.artiste_id || null,
    statut: existing?.statut || 'Brouillon',
    notes: existing?.notes || '',
  });

  useEffect(() => {
    (async () => {
      const { data: rows } = await supabase.from('artistes').select('id, nom_scene, nom, prenom, email, tel, adresse, cp, ville, siret, statut_juridique, tva_pct, tarif_base').is('deleted_at', null).order('nom_scene');
      const list = (rows || []) as ArtisteRow[];
      setArtistes(list);
      // Applique le prefill artiste une fois la liste chargée
      if (!existing && prefill?.artiste_id) {
        const a = list.find((x) => x.id === prefill.artiste_id);
        if (a) {
          setForm((f) => ({
            ...f,
            artiste_id: a.id,
            artiste_nom: a.nom_scene || [a.prenom, a.nom].filter(Boolean).join(' '),
            artiste_email: a.email || f.artiste_email,
            artiste_tel: a.tel || f.artiste_tel,
            artiste_adresse: normalizeAdresse(a.adresse, a.cp, a.ville) || f.artiste_adresse,
            artiste_siret: a.siret || f.artiste_siret,
            artiste_forme: a.statut_juridique || f.artiste_forme,
            tva_pct: a.tva_pct != null ? String(a.tva_pct) : f.tva_pct,
            remuneration_ht: (a.tarif_base != null && !f.remuneration_ht && !prefill?.prestation_id) ? String(a.tarif_base) : f.remuneration_ht,
          }));
        }
      }
      // Applique le prefill prestation (libellé client/lieu/date)
      if (!existing && prefill?.prestation_id) {
        const p = data.prest.find((x) => x.id === prefill.prestation_id);
        if (p) {
          setForm((f) => ({
            ...f,
            client_final: f.client_final || p.client,
            date_prestation: f.date_prestation || p.dateEvent || '',
            lieu: f.lieu || p.lieu || '',
            nature_prestation: f.nature_prestation || (p.themes || []).filter(Boolean).join(', ') || p.libelle || '',
            reference_contrat: f.reference_contrat || p.id,
          }));
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-calc TTC
  useEffect(() => {
    const ht = parseFloat(String(form.remuneration_ht || '').replace(',', '.'));
    const tva = parseFloat(String(form.tva_pct || '20').replace(',', '.'));
    if (Number.isFinite(ht) && Number.isFinite(tva)) {
      const ttc = ht * (1 + tva / 100);
      setForm((f) => ({ ...f, remuneration_ttc: ttc.toFixed(2) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.remuneration_ht, form.tva_pct]);

  // Sélection manuelle d'un artiste : on remplace INTÉGRALEMENT son identité et sa
  // rémunération (sinon les infos du précédent artiste restaient affichées).
  const onSelectArtiste = (id: string) => {
    const a = artistes.find((x) => x.id === id);
    if (!a) {
      setForm((f) => ({ ...f, artiste_id: null, options_remuneration: '' }));
      return;
    }
    const calc = prestArtisteCalcMap[id];
    const baseHT = calc ? (Number(calc.tarifHT) || 0) : (Number(a.tarif_base) || 0);
    let opts = 0;
    for (let n = 1; n <= 4; n++) {
      if (calc?.[`opt${n}On`]) opts += Number(calc[`opt${n}Tarif`]) || 0;
    }
    const total = baseHT + opts + fraisAnnexesHT(calc);
    setForm((f) => ({
      ...f,
      artiste_id: id,
      artiste_nom: a.nom_scene || '',
      artiste_representant: [a.prenom, a.nom].filter(Boolean).join(' '),
      artiste_email: a.email || '',
      artiste_tel: a.tel || '',
      artiste_adresse: normalizeAdresse(a.adresse, a.cp, a.ville) || '',
      artiste_siret: a.siret || '',
      artiste_forme: a.statut_juridique || '',
      tva_pct: a.tva_pct != null ? String(a.tva_pct) : f.tva_pct,
      options_remuneration: getOptionsRemuneration(calc),
      remuneration_ht: total > 0 ? String(total) : '',
    }));
  };

  const onSelectPrestation = (id: string) => {
    const p = data.prest.find((x) => x.id === id);
    if (!p) { set('prestation_id', null); set('options_remuneration', ''); setPrestArtisteIds([]); setPrestationDbId(null); return; }
    set('prestation_id', id);
    set('client_final', p.client);
    if (p.dateEvent) set('date_prestation', p.dateEvent);
    if (p.lieu) set('lieu', p.lieu);
    const natureP = (p.themes || []).filter(Boolean).join(', ') || p.libelle;
    if (natureP) set('nature_prestation', natureP);
    if (!form.reference_contrat) set('reference_contrat', p.id);
    // Reset l'artiste lié (sera réauto-sélectionné par l'effet ci-dessous)
    set('artiste_id', null);
    set('options_remuneration', '');
  };

  // Charge les artistes liés à la prestation sélectionnée (via prestations_artistes)
  useEffect(() => {
    const pid = form.prestation_id;
    if (!pid) { setPrestArtisteIds([]); setPrestationDbId(null); setPrestArtisteCalcMap({}); return; }
    (async () => {
      const dbId = await resolvePrestationDbId(pid);
      setPrestationDbId(dbId);
      if (!dbId) { setPrestArtisteIds([]); setPrestArtisteCalcMap({}); return; }
      const { data: rows } = await supabase
        .from('prestations_artistes')
        .select('artiste_id, ordre, calc_data')
        .eq('prestation_id', dbId)
        .eq('inclus', true)
        .order('ordre', { ascending: true });
      const ids = ((rows || []) as any[]).map((r) => r.artiste_id).filter(Boolean) as string[];
      const calcMap: Record<string, any> = {};
      ((rows || []) as any[]).forEach((r) => { if (r.artiste_id) calcMap[r.artiste_id] = r.calc_data; });
      setPrestArtisteIds(ids);
      setPrestArtisteCalcMap(calcMap);
      // Auto-sélection du 1er artiste lié à la prestation (modifiable manuellement)
      if (!form.artiste_id && ids.length >= 1) {
        const id = ids[0];
        const a = artistes.find((x) => x.id === id);
        if (!a) return;
        set('artiste_id', id);
        set('artiste_nom', a.nom_scene);
        const rep = [a.prenom, a.nom].filter(Boolean).join(' ');
        if (rep) set('artiste_representant', rep);
        if (a.email) set('artiste_email', a.email);
        if (a.tel) set('artiste_tel', a.tel);
        const addr = normalizeAdresse(a.adresse, a.cp, a.ville);
        if (addr) set('artiste_adresse', addr);
        if (a.siret) set('artiste_siret', a.siret);
        if (a.statut_juridique) set('artiste_forme', a.statut_juridique);
        if (a.tva_pct != null) set('tva_pct', String(a.tva_pct));
        const calc = calcMap[id];
        set('options_remuneration', getOptionsRemuneration(calc));
        const baseHT = calc ? (Number(calc.tarifHT) || 0) : (a.tarif_base ?? null);
        if (baseHT != null && !form.remuneration_ht) {
          let opts = 0;
          for (let n = 1; n <= 4; n++) {
            if (calc?.[`opt${n}On`]) {
              opts += Number(calc[`opt${n}Tarif`]) || 0;
            }
          }
          const total = baseHT + opts + fraisAnnexesHT(calc);
          if (total > 0) set('remuneration_ht', String(total));
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.prestation_id, artistes.length]);

  // Met à jour la rémunération quand calc_data devient disponible pour l'artiste déjà sélectionné (prefill)
  useEffect(() => {
    const id = form.artiste_id;
    if (!id) return;
    const calc = prestArtisteCalcMap[id];
    if (!calc) return;
    const artiste = artistes.find((x) => x.id === id);
    if (!artiste) return;
    const optionsText = getOptionsRemuneration(calc);
    if (optionsText && !form.options_remuneration) {
      setForm((f) => ({ ...f, options_remuneration: optionsText }));
    }
    const baseHT = Number(calc.tarifHT) || Number(artiste.tarif_base) || 0;
    let opts = 0;
    for (let n = 1; n <= 4; n++) {
      if (calc?.[`opt${n}On`]) {
        opts += Number(calc[`opt${n}Tarif`]) || 0;
      }
    }
    const total = baseHT + opts + fraisAnnexesHT(calc);
    if (total > 0 && !form.remuneration_ht) {
      setForm((f) => ({ ...f, remuneration_ht: String(total) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.artiste_id, prestArtisteCalcMap, artistes]);

  const generateNumero = async (): Promise<string> => {
    const year = new Date().getFullYear();
    const prefix = `CA-${year}-`;
    const { data: rows } = await supabase.from('contrats_artistes' as any).select('numero').like('numero', `${prefix}%`);
    const max = ((rows as any[]) || []).reduce((m, r) => {
      const n = parseInt((r.numero || '').slice(prefix.length), 10);
      return Number.isFinite(n) && n > m ? n : m;
    }, 0);
    return `${prefix}${String(max + 1).padStart(4, '0')}`;
  };

  const save = async () => {
    if (!form.artiste_nom?.trim()) { toast.error('Nom de l\'artiste requis'); return; }
    setSaving(true);
    try {
      const contenu: ContratArtisteData = {
        artiste_nom: form.artiste_nom, artiste_representant: form.artiste_representant, artiste_forme: form.artiste_forme,
        artiste_adresse: form.artiste_adresse, artiste_siret: form.artiste_siret, artiste_ape: form.artiste_ape,
        artiste_tel: form.artiste_tel, artiste_email: form.artiste_email, artiste_licence: form.artiste_licence,
        reference_contrat: form.reference_contrat, client_final: form.client_final, nature_prestation: form.nature_prestation,
        date_prestation: form.date_prestation, horaires: form.horaires, lieu: form.lieu,
        options_remuneration: form.options_remuneration,
        remuneration_ht: form.remuneration_ht, tva_pct: form.tva_pct, remuneration_ttc: form.remuneration_ttc,
        acompte_solde: form.acompte_solde,
        date_signature: form.date_signature, nom_signataire_artiste: form.nom_signataire_artiste,
        qualite: form.qualite, module: form.module,
        intervenants_materiel: form.intervenants_materiel,
        horaire_arrivee: form.horaire_arrivee, horaire_installation: form.horaire_installation,
        horaire_debut: form.horaire_debut, horaire_fin: form.horaire_fin, horaire_demontage: form.horaire_demontage,
        technique_rider: form.technique_rider, details_techniques: form.details_techniques,
        accueil_collations: form.accueil_collations, accueil_repas: form.accueil_repas,
        accueil_hebergement: form.accueil_hebergement, accueil_a_charge: form.accueil_a_charge,
        frais_inclus: form.frais_inclus, frais_en_sus: form.frais_en_sus,
        facturation_echeance: form.facturation_echeance, annulation_bareme: form.annulation_bareme,
        fait_a: form.fait_a,
      };
      const ttc = parseFloat(String(form.remuneration_ttc || '').replace(',', '.'));
      const ht = parseFloat(String(form.remuneration_ht || '').replace(',', '.'));
      const tva = parseFloat(String(form.tva_pct || '20').replace(',', '.'));

      const dbPrestationId = prestationDbId || (await resolvePrestationDbId(form.prestation_id));

      const payload: any = {
        prestation_id: dbPrestationId, artiste_id: form.artiste_id,
        artiste_nom: form.artiste_nom, artiste_email: form.artiste_email || null,
        artiste_tel: form.artiste_tel || null, artiste_adresse: form.artiste_adresse || null,
        artiste_siret: form.artiste_siret || null, artiste_forme: form.artiste_forme || null,
        artiste_representant: form.artiste_representant || null, artiste_ape: form.artiste_ape || null,
        artiste_licence: form.artiste_licence || null,
        client_final: form.client_final || null, nature_prestation: form.nature_prestation || null,
        date_prestation: form.date_prestation || null, horaires: form.horaires || null, lieu: form.lieu || null,
        remuneration_ht: Number.isFinite(ht) ? ht : null,
        tva_pct: Number.isFinite(tva) ? tva : null,
        remuneration_ttc: Number.isFinite(ttc) ? ttc : null,
        acompte_solde: form.acompte_solde || null,
        statut: form.statut, notes: form.notes || null,
        contenu,
      };

      if (existing) {
        const { error } = await supabase.from('contrats_artistes' as any).update(payload).eq('id', existing.id);
        if (error) throw error;
        toast.success('Contrat artiste mis à jour');
      } else {
        const numero = await generateNumero();
        const row = withDemoFlag({ ...payload, numero });
        const { error } = await supabase.from('contrats_artistes' as any).insert(row);
        if (error) throw error;
        toast.success(`Contrat ${numero} créé`);
      }
      onSaved();
    } catch (e: any) {
      toast.error(`Erreur : ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    const { previewContratArtisteHtml } = await import('../contract/generateContratArtistePdf');
    previewContratArtisteHtml(form as any);
  };
  const download = async () => {
    const { genererContratArtistePdf } = await import('../contract/generateContratArtistePdf');
    const fileName = `contrat-artiste-${(form.artiste_nom || 'artiste').replace(/[^a-z0-9]+/gi, '-')}.pdf`;
    await genererContratArtistePdf(form as any, fileName);
  };

  const inputCls = INPUT_CLS;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-start justify-center overflow-y-auto p-4">
      <div className="w-full max-w-4xl bg-slate-950 border border-slate-800 rounded-xl shadow-2xl my-8">
        <div className="flex items-center justify-between p-4 border-b border-slate-800 sticky top-0 bg-slate-950/95 backdrop-blur z-10">
          <div>
            <h2 className="text-lg font-medium text-slate-100 font-heading">{existing ? `Modifier ${existing.numero}` : 'Nouveau contrat artiste'}</h2>
            <p className="text-xs text-slate-500 mt-0.5">Pré-remplissage depuis la prestation et l'artiste sélectionnés</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={preview} className="px-3 py-2 rounded-lg text-xs bg-slate-800 border border-slate-700 text-slate-300 flex items-center gap-1.5">
              <Eye size={12} strokeWidth={1.5} /> Aperçu
            </button>
            <button onClick={download} className="px-3 py-2 rounded-lg text-xs bg-slate-800 border border-slate-700 text-slate-300 flex items-center gap-1.5">
              <Download size={12} strokeWidth={1.5} /> PDF
            </button>
            <button onClick={save} disabled={saving} className="px-3 py-2 rounded-lg text-xs bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 flex items-center gap-1.5 disabled:opacity-50">
              {saving ? <Loader2 size={12} className="animate-spin" strokeWidth={1.5} /> : <Save size={12} strokeWidth={1.5} />}
              Enregistrer
            </button>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-200"><X size={16} strokeWidth={1.5} /></button>
          </div>
        </div>

        <div className="p-5 space-y-6">
          {/* Liens dossier / artiste */}
          <section>
            <h3 className="text-xs uppercase tracking-widest text-amber-400 mb-3">Lien dossier &amp; artiste</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Prestation liée">
                <select value={form.prestation_id || ''} onChange={(e) => onSelectPrestation(e.target.value)} className={inputCls}>
                  <option value="">— Aucune —</option>
                  {data.prest.filter((p) => !p.archivedAt).map((p) => (
                    <option key={p.id} value={p.id}>{p.id} · {p.client} · {p.dateEvent || '—'}</option>
                  ))}
                </select>
              </Field>
              <Field label={`Artiste${prestArtisteIds.length ? ` (${prestArtisteIds.length} lié${prestArtisteIds.length > 1 ? 's' : ''} au dossier)` : ' (annuaire)'}`}>
                <select value={form.artiste_id || ''} onChange={(e) => onSelectArtiste(e.target.value)} className={inputCls}>
                  <option value="">— Saisie libre —</option>
                  {prestArtisteIds.length > 0 && (
                    <optgroup label="Liés au dossier">
                      {artistes.filter((a) => prestArtisteIds.includes(a.id)).map((a) => (
                        <option key={`linked-${a.id}`} value={a.id}>★ {a.nom_scene}</option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label="Tous les artistes">
                    {artistes.filter((a) => !prestArtisteIds.includes(a.id)).map((a) => (
                      <option key={a.id} value={a.id}>{a.nom_scene}</option>
                    ))}
                  </optgroup>
                </select>
              </Field>

              <Field label="Statut">
                <select value={form.statut} onChange={(e) => set('statut', e.target.value)} className={inputCls}>
                  {['Brouillon', 'À envoyer', 'Envoyé', 'Signé', 'Refusé', 'Annulé'].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Référence contrat (CP)">
                <input value={form.reference_contrat || ''} onChange={(e) => set('reference_contrat', e.target.value)} className={inputCls} placeholder="ex : CYG-2026-001" />
              </Field>
            </div>
          </section>

          {/* Artiste */}
          <section>
            <h3 className="text-xs uppercase tracking-widest text-amber-400 mb-3">Identité de l'artiste</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Nom commercial / nom de scène">
                <input value={form.artiste_nom || ''} onChange={(e) => set('artiste_nom', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Représentant (nom & prénom)">
                <input value={form.artiste_representant || ''} onChange={(e) => set('artiste_representant', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Forme juridique">
                <input value={form.artiste_forme || ''} onChange={(e) => set('artiste_forme', e.target.value)} className={inputCls} />
              </Field>
              <Field label="SIRET / SIREN">
                <input value={form.artiste_siret || ''} onChange={(e) => set('artiste_siret', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Code APE">
                <input value={form.artiste_ape || ''} onChange={(e) => set('artiste_ape', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Licence spectacle (si applicable)">
                <input value={form.artiste_licence || ''} onChange={(e) => set('artiste_licence', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Adresse" full>
                <input value={form.artiste_adresse || ''} onChange={(e) => set('artiste_adresse', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Téléphone">
                <input value={form.artiste_tel || ''} onChange={(e) => set('artiste_tel', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Email">
                <input value={form.artiste_email || ''} onChange={(e) => set('artiste_email', e.target.value)} className={inputCls} />
              </Field>
            </div>
          </section>

          {/* Synthèse prestation */}
          <section>
            <h3 className="text-xs uppercase tracking-widest text-amber-400 mb-3">Synthèse de la prestation (CP)</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Client final / événement">
                <input value={form.client_final || ''} onChange={(e) => set('client_final', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Nature de la prestation">
                <input value={form.nature_prestation || ''} onChange={(e) => set('nature_prestation', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Date de prestation">
                <input type="date" value={form.date_prestation || ''} onChange={(e) => set('date_prestation', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Horaires prévisionnels">
                <input value={form.horaires || ''} onChange={(e) => set('horaires', e.target.value)} className={inputCls} placeholder="ex : 18 h 30 – 22 h 00" />
              </Field>
              <Field label="Lieu exact" full>
                <input value={form.lieu || ''} onChange={(e) => set('lieu', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Détails techniques (remontés à l'artiste)" full>
                <textarea value={form.details_techniques || ''} onChange={(e) => set('details_techniques', e.target.value)} rows={2} className={inputCls} placeholder="ex : arrivée véhicule par la cour, prise 32A à 10 m de la scène…" />
              </Field>
            </div>
          </section>

          {/* Finances */}
          <section>
            <h3 className="text-xs uppercase tracking-widest text-amber-400 mb-3">Rémunération</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Rémunération HT (€)">
                <input value={form.remuneration_ht || ''} onChange={(e) => set('remuneration_ht', e.target.value)} className={inputCls} inputMode="decimal" />
              </Field>
              <Field label="TVA (%)">
                <TvaSelect value={form.tva_pct || 20} onChange={(v) => set('tva_pct', String(v))} className={inputCls} />
              </Field>
              <Field label="Rémunération TTC (€)">
                <input value={form.remuneration_ttc || ''} onChange={(e) => set('remuneration_ttc', e.target.value)} className={inputCls} inputMode="decimal" />
              </Field>
              <Field label="Acompte / solde" full>
                <input value={form.acompte_solde || ''} onChange={(e) => set('acompte_solde', e.target.value)} className={inputCls} placeholder="ex : 30 % à la signature, solde le jour J" />
              </Field>
              <Field label="Options comprises" full>
                <textarea value={form.options_remuneration || ''} onChange={(e) => set('options_remuneration', e.target.value)} rows={2} className={inputCls} placeholder="ex : Comprenant Option Feu" />
              </Field>
            </div>
          </section>

          {/* Qualité / documents / module */}
          <section>
            <h3 className="text-xs uppercase tracking-widest text-amber-400 mb-3">Qualité du cocontractant</h3>
            <div className="flex flex-wrap gap-4 mb-3">
              {([
                ['prestataire_technique', 'Prestataire / technique'],
                ['fournisseur', 'Fournisseur'],
                ['loueur', 'Loueur'],
                ['structure_artistique', 'Structure artistique / producteur'],
              ] as const).map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-slate-200">
                  <input type="checkbox" className="accent-amber-500" checked={!!form.qualite?.[k]}
                    onChange={(e) => set('qualite', { ...(form.qualite || {}), [k]: e.target.checked })} />
                  {label}
                </label>
              ))}
            </div>
            <Field label="Autre qualité (préciser)">
              <input value={form.qualite?.autre || ''} onChange={(e) => set('qualite', { ...(form.qualite || {}), autre: e.target.value })} className={inputCls} />
            </Field>




            <h3 className="text-xs uppercase tracking-widest text-amber-400 mt-5 mb-3">Module de la mission</h3>
            <div className="flex flex-wrap gap-4">
              {([
                ['services', 'S — Services / technique'],
                ['decoration', 'D — Décoration / fourniture'],
                ['location', 'L — Location / matériel'],
                ['artistique', 'A — Artistique / spectacle'],
              ] as const).map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-slate-200">
                  <input type="checkbox" className="accent-amber-500" checked={!!form.module?.[k]}
                    onChange={(e) => set('module', { ...(form.module || {}), [k]: e.target.checked })} />
                  {label}
                </label>
              ))}
            </div>
          </section>

          {/* Horaires détaillés & technique */}
          <section>
            <h3 className="text-xs uppercase tracking-widest text-amber-400 mb-3">Horaires détaillés &amp; technique</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Arrivée"><input value={form.horaire_arrivee || ''} onChange={(e) => set('horaire_arrivee', e.target.value)} className={inputCls} /></Field>
              <Field label="Installation / balances"><input value={form.horaire_installation || ''} onChange={(e) => set('horaire_installation', e.target.value)} className={inputCls} /></Field>
              <Field label="Début"><input value={form.horaire_debut || ''} onChange={(e) => set('horaire_debut', e.target.value)} className={inputCls} /></Field>
              <Field label="Fin"><input value={form.horaire_fin || ''} onChange={(e) => set('horaire_fin', e.target.value)} className={inputCls} /></Field>
              <Field label="Démontage / départ"><input value={form.horaire_demontage || ''} onChange={(e) => set('horaire_demontage', e.target.value)} className={inputCls} /></Field>
              <Field label="Intervenant(s) / matériel"><input value={form.intervenants_materiel || ''} onChange={(e) => set('intervenants_materiel', e.target.value)} className={inputCls} /></Field>
              <Field label="Technique / rider" full><textarea rows={2} value={form.technique_rider || ''} onChange={(e) => set('technique_rider', e.target.value)} className={inputCls} /></Field>
            </div>
          </section>

          {/* Accueil & frais */}
          <section>
            <h3 className="text-xs uppercase tracking-widest text-amber-400 mb-3">Accueil, frais et facturation</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Collations"><input value={form.accueil_collations || ''} onChange={(e) => set('accueil_collations', e.target.value)} className={inputCls} /></Field>
              <Field label="Repas"><input value={form.accueil_repas || ''} onChange={(e) => set('accueil_repas', e.target.value)} className={inputCls} /></Field>
              <Field label="Hébergement"><input value={form.accueil_hebergement || ''} onChange={(e) => set('accueil_hebergement', e.target.value)} className={inputCls} /></Field>
              <Field label="À la charge de"><input value={form.accueil_a_charge || ''} onChange={(e) => set('accueil_a_charge', e.target.value)} className={inputCls} /></Field>
              <Field label="Frais inclus"><input value={form.frais_inclus || ''} onChange={(e) => set('frais_inclus', e.target.value)} className={inputCls} /></Field>
              <Field label="Frais autorisés en sus"><input value={form.frais_en_sus || ''} onChange={(e) => set('frais_en_sus', e.target.value)} className={inputCls} /></Field>
              <Field label="Échéance de facturation"><input value={form.facturation_echeance || ''} onChange={(e) => set('facturation_echeance', e.target.value)} className={inputCls} placeholder="ex : 30 jours fin de mois" /></Field>
              <Field label="Barème d'annulation spécifique"><input value={form.annulation_bareme || ''} onChange={(e) => set('annulation_bareme', e.target.value)} className={inputCls} placeholder="vide = article 6" /></Field>
              <Field label="Fait à"><input value={form.fait_a || ''} onChange={(e) => set('fait_a', e.target.value)} className={inputCls} /></Field>
            </div>
          </section>

          {/* Signature */}
          <section>
            <h3 className="text-xs uppercase tracking-widest text-amber-400 mb-3">Signature</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Date de signature">
                <input type="date" value={form.date_signature || ''} onChange={(e) => set('date_signature', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Nom du signataire (Artiste)">
                <input value={form.nom_signataire_artiste || ''} onChange={(e) => set('nom_signataire_artiste', e.target.value)} className={inputCls} />
              </Field>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
